import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { feeCategoriesApi, FeeCategory, BuildingOption, FeePricingType } from '../../api/feeCategoriesApi';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { getDisplayPriceWithVat, getDisplayVatAmount } from '../../utils/feeCategoryVatPricing';
import { getCompositeUnitPrice } from '../../utils/feeCategoryCompositePricing';
import { formatPriceWithUnit } from '../../utils/feeQuantity';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { useCrudPermissions } from '../../hooks/useCrudPermissions';
import { FEE_CATEGORY_PERMISSIONS } from '../../constants/functionPermissions';
import { buildFeeCategoryTableRows } from '../../utils/feeCategoryCodeGrouping';

interface FeeCategoryListProps {
  
}

const FeeCategoryList: React.FC<FeeCategoryListProps> = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const { user } = useAuth();
  const { canCreate, canEdit, canDelete, canImport } = useCrudPermissions(FEE_CATEGORY_PERMISSIONS);
  const [feeCategories, setFeeCategories] = useState<FeeCategory[]>([]);
  const [buildings, setBuildings] = useState<BuildingOption[]>([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'skip' | 'replace'>('skip');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadFeeCategories();
    } else {
      setFeeCategories([]);
    }
  }, [selectedBuildingId]);

  const loadBuildings = async () => {
    try {
      const data = await feeCategoriesApi.getBuildingsForUser();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToLoad')));
    }
  };

  const loadFeeCategories = async () => {
    if (!selectedBuildingId) return;
    
    setLoading(true);
    try {
      const data = await feeCategoriesApi.getAll(selectedBuildingId);
      setFeeCategories(data);
      setError(null);
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToLoadCategories')));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('confirmDeleteFeeCategory'))) {
      return;
    }

    try {
      await feeCategoriesApi.delete(id);
      await loadFeeCategories();
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToDelete')));
    }
  };

  const handleStatusToggle = async (id: string, currentStatus: boolean) => {
    try {
      await feeCategoriesApi.update(id, { isActive: !currentStatus });
      await loadFeeCategories();
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToUpdateStatus')));
    }
  };

  const handleSeedDefault = async () => {
    if (!selectedBuildingId) {
      setError(t('pleaseSelectBuilding'));
      return;
    }

    try {
      setSeeding(true);
      setError(null);
      const append = feeCategories.length > 0;
      const result = await feeCategoriesApi.seedDefault(selectedBuildingId, append);
      await loadFeeCategories();
      alert(result.message || t('defaultFeeCategoriesCreatedSuccess'));
    } catch (err) {
      setError(getApiErrorMessage(err, t('errorCreatingDefaultFeeCategories')));
    } finally {
      setSeeding(false);
    }
  };

  const handleExport = async () => {
    if (!selectedBuildingId) {
      setError(t('pleaseSelectBuilding'));
      return;
    }

    try {
      setExporting(true);
      setError(null);
      await feeCategoriesApi.exportCategories(selectedBuildingId);
      alert(t('exportFeeCategoriesSuccess'));
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToLoad')));
    } finally {
      setExporting(false);
    }
  };

  const openImportModal = () => {
    setImportFile(null);
    setImportError(null);
    setImportSuccess(null);
    setImportMode('skip');
    setShowImportModal(true);
  };

  const handleImport = async () => {
    if (!selectedBuildingId) {
      setImportError(t('pleaseSelectBuilding'));
      return;
    }
    if (!importFile) {
      setImportError(t('clickToSelectFile'));
      return;
    }

    try {
      setImportLoading(true);
      setImportError(null);
      setImportSuccess(null);
      const result = await feeCategoriesApi.importCategories(selectedBuildingId, importFile, importMode);

      let message = result.message;
      if (result.skippedCount > 0 && result.skippedCodes.length > 0) {
        message += ` (${result.skippedCodes.join(', ')})`;
      }
      if (result.errors.length > 0) {
        message += `\n${result.errors.join('\n')}`;
      }

      if (result.success) {
        setImportSuccess(message);
        await loadFeeCategories();
      } else {
        setImportError(message);
      }
    } catch (err) {
      setImportError(getApiErrorMessage(err, t('failedToLoad')));
    } finally {
      setImportLoading(false);
    }
  };

  const handleServiceNameClick = (id: string) => {
    if (!canEdit) return;
    navigate(`/fee-categories/${id}/edit`);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN').format(amount);
  };

  const getPricingTypeText = (category: FeeCategory) => {
    switch (category.pricingType) {
      case FeePricingType.SINGLE_TIER:
        return t('singleTier');
      case FeePricingType.TIERED:
        return t('tiered');
      case FeePricingType.QUOTA_BASED:
        return t('quotaBased');
      default:
        return t('unknown');
    }
  };

  const getDisplayPrice = (category: FeeCategory) => {
    if (category.pricingType === FeePricingType.SINGLE_TIER) {
      if (category.basePrice) {
        const priceWithVAT = getDisplayPriceWithVat(category.basePrice, category.vatPercentage, category.vatAmount);
        const formattedPrice = formatCurrency(priceWithVAT);
        
        // Special handling for parking fees - show "/ xe" for consistency
        if (category.name.toLowerCase().includes('xe') && !category.quantityUnit) {
          return `${formattedPrice} / xe`;
        }

        return formatPriceWithUnit(formattedPrice, category);
      }
      return '-';
    } else if (category.pricingType === FeePricingType.TIERED) {
      return category.feeTiers.length > 0 ? t('tiered') : '-';
    } else if (category.pricingType === FeePricingType.QUOTA_BASED) {
      // For quota-based pricing, show the effective prices with units
      if (category.quotaPrice) {
        const formattedPrice = formatCurrency(category.quotaPrice);
        
        // Determine unit based on fee category name
        let unit = 'đơn vị';
        if (category.name.toLowerCase().includes('nước')) {
          unit = 'm³';
        } else if (category.name.toLowerCase().includes('điện')) {
          unit = 'kWh';
        } else if (category.name.toLowerCase().includes('xe')) {
          unit = 'xe';
        }
        
        return `${formattedPrice} / ${unit}`;
      }
      return t('notConfigured');
    }
    return '-';
  };

  const getTotalAmount = (category: FeeCategory) => {
    if (category.pricingType === FeePricingType.SINGLE_TIER) {
      const unitPrice = getCompositeUnitPrice({
        basePrice: category.basePrice,
        vatPercentage: category.vatPercentage,
        vatAmount: category.vatAmount,
        extraFee1Percent: category.extraFee1Percent,
        extraFee1Amount: category.extraFee1Amount,
        extraFee1VatPercent: category.extraFee1VatPercent,
        extraFee1VatAmount: category.extraFee1VatAmount,
        extraFee1Fixed: category.extraFee1Fixed,
        extraFee2Percent: category.extraFee2Percent,
        extraFee2Amount: category.extraFee2Amount,
        extraFee2VatPercent: category.extraFee2VatPercent,
        extraFee2VatAmount: category.extraFee2VatAmount,
        extraFee2Fixed: category.extraFee2Fixed,
        totalUnitPrice: category.totalUnitPrice,
      });
      return formatCurrency(unitPrice);
    }
    return t('manual');
  };

  const getVatAmountDisplay = (category: FeeCategory) => {
    if (category.pricingType === FeePricingType.SINGLE_TIER && category.basePrice) {
      return formatCurrency(getDisplayVatAmount(category.basePrice, category.vatPercentage, category.vatAmount));
    }
    return category.vatPercentage > 0 ? `${category.vatPercentage}%` : '-';
  };

  const selectedBuilding = buildings.find(b => b.id === selectedBuildingId);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-800">{t('feeCategories')}</h1>
        </div>
        <div className="flex gap-3">
          {canCreate && (
            <Link
              to="/fee-categories/add"
              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <span>⊕</span>
              {t('addFeeService')}
            </Link>
          )}
          
          <button
            onClick={handleExport}
            disabled={exporting || !selectedBuildingId}
            className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors duration-200 disabled:opacity-50"
          >
            {exporting ? t('loading') : t('exportFeeCategories')}
          </button>

          {canImport && (
            <button
              onClick={openImportModal}
              disabled={!selectedBuildingId}
              className="inline-flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors duration-200 disabled:opacity-50"
            >
              {t('importFeeCategories')}
            </button>
          )}

          {canCreate && (
            <button
              onClick={handleSeedDefault}
              disabled={seeding || !selectedBuildingId}
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {seeding ? t('creating') : t('createDefaultFeeCategories')}
            </button>
          )}
        </div>
      </div>

      {/* Building Selection */}
      {shouldShowBuildingSelector(buildings) && (
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-4">
          <label className="font-medium text-gray-700 whitespace-nowrap">{t('selectBuilding')}</label>
          <select
            value={selectedBuildingId}
            onChange={(e) => selectBuilding(user?.id, e.target.value, setSelectedBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-0 flex-1 max-w-sm"
          >
            <option value="">{t('selectBuildingPlaceholder')}</option>
            {buildings.map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </select>
          {selectedBuilding && (
            <span className="text-gray-600 text-sm whitespace-nowrap">
              {t('manager')} {selectedBuilding.operator.name}
            </span>
          )}
        </div>
      </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Fee Categories Table */}
      {selectedBuildingId && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="text-gray-500">{t('loading')}</div>
            </div>
          ) : feeCategories.length === 0 ? (
            <div className="p-8 text-center">
              <div className="text-gray-500 mb-4">{t('noFeeCategories')}</div>
              <div className="space-x-3">
                {canCreate && (
                  <Link
                    to="/fee-categories/add"
                    className="text-blue-500 hover:text-blue-600"
                  >
                    {t('addFirstFee')}
                  </Link>
                )}
                {canCreate && (
                  <button
                    onClick={handleSeedDefault}
                    disabled={seeding || !selectedBuildingId}
                    className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
                  >
                    {seeding ? t('creating') : t('seedDefaultFeeCategories')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      {t('serviceNumber')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      {t('feeCode')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-48">
                      {t('feeService')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      {t('feeType')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      {t('unitPrice')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                      {t('vat')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      {t('totalAmount')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      {t('closeFee')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      {t('status')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-32">
                      {t('notes')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      {t('actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {buildFeeCategoryTableRows(feeCategories).map(({ item: category, isChild }, index) => (
                    <tr
                      key={category.id}
                      className={`hover:bg-gray-50 transition-colors ${isChild ? 'bg-gray-50/60' : ''}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {index + 1}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-mono text-gray-700">
                        {category.feeCode || '-'}
                      </td>
                      <td className="px-4 py-4">
                        {canEdit ? (
                          <button
                            onClick={() => handleServiceNameClick(category.id)}
                            className={`text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer underline text-left ${isChild ? 'pl-4' : ''}`}
                          >
                            {category.name}
                          </button>
                        ) : (
                          <span
                            className={`text-sm font-medium text-gray-900 ${isChild ? 'pl-4' : ''}`}
                          >
                            {category.name}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getPricingTypeText(category)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getDisplayPrice(category)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {getVatAmountDisplay(category)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getTotalAmount(category)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        {category.autoCloseMonthly ? t('automatic') : t('manual')}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {canEdit ? (
                          <button
                            onClick={() => handleStatusToggle(category.id, category.isActive)}
                            className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full cursor-pointer transition-colors ${
                              category.isActive
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                            }`}
                          >
                            {category.isActive ? t('active') : t('inactive')}
                          </button>
                        ) : (
                          <span
                            className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                              category.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {category.isActive ? t('active') : t('inactive')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900 max-w-xs">
                        <div className="truncate" title={category.notes || ''}>
                          {category.notes || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                        {(canEdit || canDelete) && (
                          <div className="flex gap-2">
                            {canEdit && (
                              <Link
                                to={`/fee-categories/${category.id}/edit`}
                                className="text-blue-600 hover:text-blue-900 transition-colors"
                              >
                                ✎ {t('edit')}
                              </Link>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => handleDelete(category.id)}
                                className="text-red-600 hover:text-red-900 transition-colors"
                              >
                                🗑 {t('delete')}
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">{t('importFeeCategories')}</h2>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              {!selectedBuildingId ? (
                <div className="text-sm text-gray-600">{t('pleaseSelectBuilding')}</div>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-4">{t('importFeeCategoriesHint')}</p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('selectBuilding')}</label>
                    <div className="text-sm text-gray-800 font-medium">{selectedBuilding?.name}</div>
                  </div>

                  <div className="mb-4 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="importMode"
                        checked={importMode === 'skip'}
                        onChange={() => setImportMode('skip')}
                      />
                      <span className="text-sm text-gray-700">{t('importFeeCategoriesModeSkip')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="importMode"
                        checked={importMode === 'replace'}
                        onChange={() => setImportMode('replace')}
                      />
                      <span className="text-sm text-gray-700">{t('importFeeCategoriesModeReplace')}</span>
                    </label>
                  </div>

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center mb-4">
                    <input
                      type="file"
                      accept=".json,application/json"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="fee-category-import-file"
                    />
                    <label htmlFor="fee-category-import-file" className="cursor-pointer flex flex-col items-center space-y-2">
                      <span className="text-sm text-gray-700">
                        {importFile ? importFile.name : t('clickToSelectFile')}
                      </span>
                      <span className="text-xs text-gray-500">{t('feeCategoryImportJsonOnly')}</span>
                    </label>
                  </div>

                  {importError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm whitespace-pre-line">
                      {importError}
                    </div>
                  )}

                  {importSuccess && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 text-sm whitespace-pre-line">
                      {importSuccess}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {t('close')}
              </button>
              <button
                onClick={handleImport}
                disabled={!importFile || importLoading || !selectedBuildingId}
                className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-6 py-2 rounded-lg transition-colors"
              >
                {importLoading ? t('importLoading') : t('importFeeCategories')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeCategoryList; 